import React, { FC, ReactElement, useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Badge,
  Button,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  PopoverBody,
  UncontrolledDropdown,
  UncontrolledPopover,
} from 'reactstrap';
import { OptionDataPayload, OptionDataValue } from '../../../shared/options';
import * as OrderSummary from '../../../shared/orders/model';
import { ProductCategoryModel, ProductModel } from '../../../shared/orders/product';
import {
  AgeRestrictedProductBadge,
  InlineHelp,
  MaterialIcon,
  OrderPaymentList,
  PaymentState,
  ProductOptionViewer,
} from '../../components';
import { ActionModal } from '../../components/ActionModal';
import { Currency } from '../../components/Currency';
import { MaterialIconText } from '../../components/MaterialIconText';
import { OptionViewer } from '../../components/OptionViewer';
import { useTranslation } from '../../translations';
import {
  classNames,
  displayName,
  Fetcher,
  handleUnknownOptionError,
  isLogicError,
  sumBy,
  useFetcher,
  useNumericInputState,
} from '../../utils';
import { useDebounceValue } from '../../utils/debounce';
import { captureError, LogicError } from '../../utils/errorHandling';

interface OrderItemProductProps {
  readonly item: OrderSummary.OrderItem;
  readonly order: OrderSummary.Order;
  readonly readOnly?: boolean;
  readonly cashierUI?: boolean;
  readonly adminControls?: boolean;
  readonly onUpdate?: () => void;
}

export const OrderItemProduct: FC<OrderItemProductProps> = ({
  cashierUI,
  order,
  item,
  readOnly,
  adminControls,
  onUpdate,
}) => {
  const [quantity, setQuantityEv, setQuantity] = useNumericInputState(item.quantity);
  const [lastSafeQuantity, setLastSafeQuantity] = useState(item.quantity);
  const debouncedQuantity = useDebounceValue<number>(quantity, 350);
  const canRemoveStock = item.stock && item.stock > 0 && item.referenceId === null;

  const { product } = item;

  const deleteItem = async () => {
    if (readOnly) {
      return;
    }

    await api.removeProductFromCart(order.id, { orderItemId: item.id });
    if (onUpdate) {
      onUpdate();
    }
  };

  useEffect(() => {
    const updateItemQuantity = async () => {
      if (readOnly || !item.product.category.isStore || debouncedQuantity === lastSafeQuantity) {
        return;
      }

      try {
        await api.updateProductQuantity(order.id, item.id, quantity, adminControls);
        setLastSafeQuantity(quantity);
      } catch (error) {
        if (isLogicError(error, LogicError.UnknownProductOption)) {
          handleUnknownOptionError(error.apiResponse.errors.logic, item.options);
        } else {
          setQuantity(lastSafeQuantity);
          captureError(error as Error);
        }
      }

      if (onUpdate) {
        onUpdate();
      }
    };

    void updateItemQuantity();
  }, [debouncedQuantity, lastSafeQuantity, item]);

  const { ts } = useTranslation();
  const isProductFree = item.price === 0;

  if (!adminControls && !cashierUI && item.parentOrderItemId) {
    const parent = order.orderItems.find((t) => t.id === item.parentOrderItemId);

    if (parent) {
      const addonFromParent = parent.product.addons.find((t) => t.addonId === item.product.id);
      if (addonFromParent?.hidden) {
        return null;
      }
    }
  }

  return (
    <div className="order-line mb-10" id={`orderItem-${item.id}`}>
      <div className="order-item">
        <div className="order-item-description">
          {readOnly && item.quantity > 1 && `${item.quantity} x `}
          {displayName(product)} &mdash; {product.category.name}
          {order.status === 'paid' && <GrantStatus orderItem={item} />}
          <br />
          <small>
            {ts('common.product_id')} - {product.id}
          </small>
          {!!item.userId && (
            <small>
              , {ts('common.user_id')} - {item.userId}
            </small>
          )}
          {item.userId !== order.userId && (
            <small>
              , <AdminUserLink adminControls={adminControls} userId={item.userId} />
            </small>
          )}
          <div className="badge-list compact">
            {cashierUI && (product.maxAge || product.minAge) && (
              <AgeRestrictedProductBadge
                audience="Customer"
                idPrefix={`orderItem${item.id}`}
                maxAge={product.maxAge ?? undefined}
                minAge={product.minAge ?? undefined}
                title={ts('utils_verify_age')}
              />
            )}
            {cashierUI && canRemoveStock && (
              <Badge
                className="with-inline-icon"
                color="danger"
                id={`orderItem${item.id}unusedStock`}
                pill
              >
                {ts('utils.unused_stock')}&nbsp;
                <InlineHelp>{ts('utils.unused_stock_help')}</InlineHelp>
              </Badge>
            )}
          </div>
          {item.options.length > 0 && <OptionViewer optionOrderItems={item.options} />}
          {item.parentOrderItemId && (
            <div className="order-item-addons">
              {ts('utils.free_addon')}{' '}
              {displayName(
                order.orderItems.find((t) => t.id === item.parentOrderItemId)?.product ?? {},
              )}
            </div>
          )}
        </div>
        {!readOnly && !item.parentOrderItemId && (
          <div className="order-item-price">
            {canEditProductQuantity(product) && (
              <Input
                id="quantity"
                min="1"
                name="quantity"
                onChange={setQuantityEv}
                type="number"
                value={quantity}
              />
            )}
            {(cashierUI || product.category.isStore) && !item.parentOrderItemId && (
              <div className="order-item-delete" onClick={deleteItem}>
                {ts('common.delete')}
              </div>
            )}
          </div>
        )}

        <div className={classNames({ 'text-success': isProductFree }, 'order-item-price')}>
          <OrderItemControls
            adminControls={adminControls}
            item={item}
            order={order}
            reload={onUpdate}
          />
          {isProductFree ? ts('common.free') : <Currency value={item.price} />}
        </div>
      </div>
      {item.surcharges.map((surcharge) => (
        <OrderItemProductSurcharge item={item} key={surcharge.id} surcharge={surcharge} />
      ))}
      {item.surcharges.length > 0 && (
        <div className="order-surcharge-total" id={`orderItem-${item.id}-surcharge-total`}>
          <div className="order-item-description">
            <small>{ts('utils.total_w_surcharges')}</small>
          </div>
          <div className="order-item-price">
            <strong>
              <Currency value={item.price + sumBy(item.surcharges, (s) => s.totalAmount)} />
            </strong>
          </div>
        </div>
      )}
    </div>
  );
};

const AdminUserLink: FC<{ readonly adminControls?: boolean; readonly userId: number }> = ({
  adminControls,
  userId,
}) => {
  const { ts } = useTranslation();

  if (!adminControls) {
    return (
      <>
        {ts('common.user')} - {userId}
      </>
    );
  }

  return (
    <>
      {ts('common.user')} - <Link to={`/housekeeping/attendees/user/${userId}`}>{userId}</Link>
    </>
  );
};

const OrderItemProductSurcharge: FC<{
  readonly item: OrderSummary.OrderItem;
  readonly surcharge: OrderSummary.Surcharge;
}> = ({ item, surcharge }) => {
  const { amount, amountType, type } = surcharge;
  const { ts } = useTranslation();
  const surchargeAmountText = amountType === 'fixed' ? null : `@ ${(amount / 100).toFixed(2)}%`;

  return (
    <div className="order-surcharge-item" id={`orderItem-${item.id}-surcharge-${surcharge.id}`}>
      <div className="order-item-description">
        <small>
          {ts(type === 'tax' ? 'common.tax' : 'common.fee_surcharge')} &mdash;{' '}
          {surcharge.displayName ?? surcharge.name} {surchargeAmountText}
        </small>
      </div>
      <div className="order-item-price">
        {surcharge.totalAmount > 0 ? '+' : '-'}{' '}
        <Currency
          value={surcharge.totalAmount > 0 ? surcharge.totalAmount : -surcharge.totalAmount}
        />
      </div>
    </div>
  );
};

const OrderItemControls: FC<{
  readonly order: OrderSummary.Order;
  readonly item: OrderSummary.OrderItem;
  readonly adminControls?: boolean;
  readonly reload?: () => void;
}> = ({ order, item, adminControls, reload }) => {
  const { ts } = useTranslation();
  const setActiveRegistration = useCallback(async () => {
    try {
      await api.setActiveRegistrationPayment(item.id);
      if (reload) {
        reload();
      }
    } catch (error) {
      captureError(error as Error);
    }
  }, [item.id, reload]);

  const undoDelivery = useCallback(async () => {
    try {
      await api.undoOrderItemDelivery(order.id, item.id);
      if (reload) {
        reload();
      }

      toast.success(ts('saved_successfully'));
    } catch (error) {
      captureError(error as Error);
    }
  }, [item.id, order.id, reload, ts]);

  const [modal, setModal] = useState<'editOrderItem' | 'removeStock' | undefined>();
  const closeModal = useCallback(() => {
    setModal(undefined);
  }, [setModal]);

  const itemOrParentIsRegistration =
    item.product.category.isRegistration ||
    (order.orderItems.find((oi) => oi.id === item.parentOrderItemId)?.product.category
      .isRegistration ??
      false);

  const shouldSetActiveRegistration =
    adminControls &&
    item.product.category.isRegistration &&
    !item.referenceId &&
    order.status === 'paid';

  const isFulfilled = adminControls && item.dispatchedOn !== null;
  const canRemoveStock = adminControls && item.stock && item.stock > 0 && item.referenceId === null;
  const hasOptionsToEdit = !itemOrParentIsRegistration && item.options.length > 0;

  const menuItems: ReactElement[] = [];

  if (shouldSetActiveRegistration) {
    menuItems.push(
      <DropdownItem className="orderItem-set-active" onClick={setActiveRegistration}>
        {ts('utils.set_as_active_registration')}
      </DropdownItem>,
    );
  }

  if (canRemoveStock) {
    menuItems.push(
      <DropdownItem
        className="orderItem-removeStock"
        onClick={() => {
          setModal('removeStock');
        }}
      >
        {ts('utils.remove_unused_stock')}
      </DropdownItem>,
    );
  }

  if (isFulfilled) {
    menuItems.push(
      <DropdownItem className="orderItem-undo-delivery" onClick={undoDelivery}>
        {ts('utils.undo_delivery')}
      </DropdownItem>,
    );
  }

  if (!isFulfilled && hasOptionsToEdit) {
    menuItems.push(
      <DropdownItem
        className="orderItem-edit-options"
        onClick={() => {
          setModal('editOrderItem');
        }}
      >
        {ts('order.edit_option_values')}
      </DropdownItem>,
    );
  }

  if (menuItems.length === 0) {
    return null;
  }

  return (
    <div>
      {modal === 'editOrderItem' && (
        <OptionEditorModal
          admin={adminControls}
          close={closeModal}
          orderId={order.id}
          orderItem={item}
          reload={reload}
        />
      )}
      {adminControls && modal === 'removeStock' && (
        <RemoveStockModal close={closeModal} orderId={order.id} orderItem={item} reload={reload} />
      )}
      <UncontrolledDropdown>
        <DropdownToggle id={`orderItemDropdown${item.id}`} tag="a">
          <MaterialIcon className="action-more-options clickable" name="more_horiz" />
        </DropdownToggle>
        <DropdownMenu end>{menuItems}</DropdownMenu>
      </UncontrolledDropdown>
    </div>
  );
};

const RemoveStockModal: FC<{
  readonly orderId: number;
  readonly orderItem: OrderSummary.OrderItem;
  readonly close: () => void;
  readonly reload?: () => void;
}> = ({ orderItem, close, orderId, reload }) => {
  const { ts } = useTranslation();

  const onComplete = useCallback(async () => {
    try {
      close();
      await api.removeStock(orderId, orderItem.id);
      toast.success(ts('utils.stock_removed'));
    } catch (error) {
      captureError(error as Error);
    }

    reload?.();
  }, [close, orderId, orderItem.id, reload, ts]);

  return (
    <ActionModal
      actionContent="remove"
      close={close}
      color="danger"
      id="removeStockModal"
      isOpen
      onComplete={onComplete}
      title="utils.remove_unused_stock"
    >
      <p>{ts('utils.explain_remove_stock')}</p>
    </ActionModal>
  );
};

const OptionEditorModal: FC<{
  readonly orderId: number;
  readonly orderItem: OrderSummary.OrderItem;
  readonly admin?: boolean;
  readonly close: () => void;
  readonly reload?: () => void;
}> = ({ orderItem, close, orderId, admin, reload }) => {
  const [payload, setPayload] = useState<OptionDataPayload>({});
  const options = useFetcher(async () => {
    const data = await api.getPublicProductById(orderItem.product.id);

    const result: OptionDataPayload = {};
    for (const option of data.options) {
      const orderItemOption = orderItem.options.filter((i) => i.optionId === option.id);

      if (orderItemOption.length === 0) {
        continue;
      }

      if (orderItemOption.length > 1) {
        const old = (result[option.id] ?? []) as string[];
        result[option.id] = [...old, ...orderItemOption.map((oio) => oio.value ?? '')];
      } else {
        result[option.id] = orderItemOption[0]!.value ?? '';
      }
    }

    setPayload(result);

    return data;
  });

  const { ts } = useTranslation();

  const setOptionValue = useCallback(
    (id: number, value: OptionDataValue) => {
      setPayload((prev) => ({
        ...prev,
        [id]: value,
      }));
    },
    [setPayload],
  );

  const saveData = useCallback(async () => {
    close();
    try {
      await api.updateOrderItemOption(orderId, orderItem.id, payload, admin);
    } catch (error) {
      captureError(error as Error);
    }

    reload?.();
  }, [admin, close, orderId, orderItem.id, payload, reload]);

  return (
    <Modal isOpen>
      <ModalHeader>{ts('order.item.update_option')}</ModalHeader>
      <ModalBody>
        {options.loading && <Fetcher inline result={options} />}
        {options.data?.options.map((o) => (
          <ProductOptionViewer
            adminView={admin}
            defaultValue={payload[o.id]}
            disabled={!o.mutable && !admin}
            key={o.id}
            onChange={setOptionValue}
            option={o}
          />
        ))}
      </ModalBody>
      <ModalFooter>
        <Button color="primary" onClick={saveData}>
          {ts('common.save')}
        </Button>
        <Button onClick={close}>{ts('common.cancel')}</Button>
      </ModalFooter>
    </Modal>
  );
};

export const GrantStatus: FC<{ readonly orderItem: OrderSummary.OrderItem }> = ({ orderItem }) => {
  const { ts } = useTranslation();
  const id = `orderItemGrant-${orderItem.id}`;
  return (
    <span className="grant-status">
      <span className="inline-icon" id={id}>
        <MaterialIcon
          className={orderItem.referenceId ? 'yes' : 'no'}
          name={orderItem.referenceId ? 'done' : 'close'}
          type={orderItem.referenceId ? 'success' : 'danger'}
        />
      </span>
      <UncontrolledPopover placement="bottom" target={id} trigger="hover">
        <PopoverBody>
          <p>
            {ts(
              orderItem.referenceId
                ? 'utils.this_user_has_an_active'
                : 'utils.this_order_item_is_not',
            )}
          </p>
        </PopoverBody>
      </UncontrolledPopover>
    </span>
  );
};

interface ProductCanEditProps extends Pick<ProductModel, 'isTicketed' | 'maxGrants'> {
  category: {
    isStore: boolean;
  };
}

export function canEditProductQuantity(product: ProductCanEditProps): boolean {
  return (
    product.category.isStore && !product.isTicketed && (!product.maxGrants || product.maxGrants > 1)
  );
}

interface RenderOrderSummaryProps {
  readonly order: OrderSummary.Order;
  readonly categories: ProductCategoryModel[];
  readonly paymentState?: PaymentState;
  readonly isCustomer?: boolean;
  readonly readOnly?: boolean;
}

export const RenderOrderSummary: FC<RenderOrderSummaryProps> = ({
  order,
  categories,
  isCustomer,
  paymentState,
  readOnly,
}) => {
  const isPaid = readOnly ?? paymentState === PaymentState.Success;
  const taxSurcharges = sumBy(order.orderItems, (i) => sumBy(i.surcharges, (s) => s.totalAmount));
  const { ts } = useTranslation();
  return (
    <>
      {categories.map((category) => renderCategoryTotal(order.orderItems, category))}
      {order.fees.length > 0 && (
        <div className="order-summary-item">
          <div className="order-summary-description">{ts('common.fees_charges_and_fines')}</div>
          <div className="order-summary-price">
            <Currency value={sumBy(order.fees, (fee) => fee.amount)} />
          </div>
        </div>
      )}
      <hr />
      <div className="order-summary-item">
        <div className="order-summary-description">{ts('utils.taxes_surcharges')}</div>
        <div className="order-summary-price">
          <Currency value={taxSurcharges} />
        </div>
      </div>
      {order.vouchers.map((v) => (
        <VoucherInfo key={v.code} voucher={v} />
      ))}
      <OrderPaymentList isCustomer={isCustomer ?? false} isPaid={isPaid} order={order} />
      {order.breakdown.refunds > 0 && isPaid && (
        <div className="text-success order-total">
          <div className="order-total-description">{ts('utils.refund_total')}</div>
          <div className="order-total-price">
            - <Currency value={order.breakdown.refunds} />
          </div>
        </div>
      )}
    </>
  );
};

interface VoucherInfoProps {
  readonly voucher: OrderSummary.Voucher;
}

const VoucherInfo: FC<VoucherInfoProps> = ({
  voucher: { amount, code, id, displayName: dName },
}) => {
  const { ts } = useTranslation();
  return (
    <div className="order-discount-item" id={`vouchers${id ?? '-new'}`}>
      <div className="order-discount-description text-success">
        {ts('utils.discount')}: {dName ? `${dName} (${code})` : code}
      </div>
      <div className="order-discount-price text-success">
        - <Currency value={amount} />
      </div>
    </div>
  );
};

export function renderCategoryTotal(
  items: OrderSummary.OrderItem[],
  category: ProductCategoryModel,
): React.JSX.Element | null {
  const applicableItems = items.filter(({ product }) => product.category.id === category.id);

  if (applicableItems.length === 0) {
    return null;
  }

  const total = sumBy(applicableItems, (item) => item.price * item.quantity);

  return (
    <div className="order-summary-item" key={category.id}>
      <div className="order-summary-description">{category.name}</div>
      <div className="order-summary-price">
        <Currency value={total} />
      </div>
    </div>
  );
}

export const OrderRefundItem: FC<{
  readonly refund: OrderSummary.Refund;
  readonly orderItems: OrderSummary.OrderItem[];
}> = ({ refund, orderItems }) => {
  const { ts } = useTranslation();

  return (
    <div className="order-line" key={refund.id}>
      <div className="order-item">
        <div className="order-item-description">
          {ts('refund')} #{refund.id} - {ts('common.status')}: {refund.status}
        </div>
        <div className="order-item-price">
          -<Currency value={refund.amount} />
        </div>
      </div>
      {refund.items.map((refundItem) => {
        const orderItem = orderItems.find((t) => t.id === refundItem.itemId);

        if (!orderItem) {
          return null;
        }

        return (
          <RefundItemDisplay
            key={refundItem.itemId}
            orderItem={orderItems.find((t) => t.id === refundItem.itemId)!}
            refund={refund}
            refundItem={refundItem}
          />
        );
      })}
    </div>
  );
};

interface RefundItemProps {
  readonly refund: OrderSummary.Refund;
  readonly refundItem: OrderSummary.RefundItem;
  readonly orderItem: OrderSummary.OrderItem;
}

const RefundItemDisplay: FC<RefundItemProps> = ({ refund, refundItem, orderItem }) => {
  const { product } = orderItem;
  const showQuantity = refundItem.quantity > 1 || orderItem.quantity > 1;

  return (
    <div id={`refundItem-${refundItem.itemId}`}>
      <div className="order-item">
        <div className="order-item-description">
          <MaterialIconText medium name="delete_forever" type="danger">
            {showQuantity && `-${refundItem.quantity} x `}
            {displayName(product)} &mdash; {product.category.name}
            <br />
            <small>
              {refund.status} - {refund.requestReason} - {refund.requestAddInfo}
            </small>
          </MaterialIconText>
        </div>
      </div>
    </div>
  );
};
